/**
 * Inline Manufacturer Autocomplete Component
 * Compact version designed for table cell usage in the VisualTableEditor.
 * Uses onMouseDown with e.preventDefault() to ensure single-click selection.
 * Uses React Portal to render dropdown outside overflow containers.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Check, Plus, Loader2 } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import api from '../../services/api';

const InlineManufacturerAutocomplete = ({
  value,
  onChange,
  onFocus,
  onKeyDown,
  cellRef,
  className = "",
  placeholder = "",
  testId = "manufacturer-cell"
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const isSelectingRef = useRef(false);
  const isUserClickRef = useRef(false); // Track if focus came from user click

  // Sync external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Expose input ref to parent
  useEffect(() => {
    if (cellRef) {
      cellRef(inputRef.current);
    }
  }, [cellRef]);

  // Calculate dropdown position when opening
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: Math.max(rect.width, 280)
      });
    }
  }, []);

  // Update position on scroll/resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      const handleScroll = () => updateDropdownPosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Load suggestions from API
  const loadSuggestions = useCallback(async (query) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/v1/parts/manufacturers/autocomplete`, {
        params: { q: query, limit: 6 }
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to load manufacturer suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useDebouncedCallback(loadSuggestions, 200);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHighlightedIndex(-1);
    
    if (newValue.trim()) {
      setIsOpen(true);
      debouncedSearch(newValue);
    } else {
      setIsOpen(true);
      loadSuggestions('');
    }
    
    // Also update parent immediately for visual feedback
    onChange(newValue);
  };

  // Handle suggestion selection - CRITICAL: uses onMouseDown with preventDefault
  const handleSelect = (manufacturer) => {
    isSelectingRef.current = true;
    const name = typeof manufacturer === 'string' ? manufacturer : manufacturer.name;
    setInputValue(name);
    onChange(name);
    setIsOpen(false);
    setHighlightedIndex(-1);
    
    // Reset the flag after a short delay
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  // Handle "Create new" option
  const handleCreateNew = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      isSelectingRef.current = true;
      onChange(trimmedValue);
      setIsOpen(false);
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    }
  };

  // Handle focus - only open dropdown on user click, not programmatic focus
  const handleFocus = (e) => {
    updateDropdownPosition();
    
    // Only auto-open dropdown if triggered by user click
    // NOT on programmatic focus (Tab navigation, addRow, etc.)
    if (isUserClickRef.current) {
      setIsOpen(true);
      if (!suggestions.length) {
        loadSuggestions(inputValue || '');
      }
    }
    
    // Reset the flag
    isUserClickRef.current = false;
    
    // Call parent's onFocus if provided
    if (onFocus) {
      onFocus(e);
    }
  };

  // Track user clicks to distinguish from programmatic focus
  const handleMouseDown = () => {
    isUserClickRef.current = true;
  };

  // Handle blur with delay for click events
  const handleBlur = () => {
    setTimeout(() => {
      if (isSelectingRef.current) {
        return;
      }
      setIsOpen(false);
      // Commit current value on blur
      if (inputValue.trim() && inputValue !== value) {
        onChange(inputValue.trim());
      }
    }, 150);
  };

  // Keyboard navigation - merged with parent's onKeyDown
  const handleKeyDown = (e) => {
    // Handle autocomplete-specific keys first
    if (isOpen) {
      const maxIndex = suggestions.length; // +1 for "Create new" option

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex(prev => Math.min(prev + 1, maxIndex));
          return;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex(prev => Math.max(prev - 1, -1));
          return;
        case 'Enter':
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            e.preventDefault();
            e.stopPropagation();
            handleSelect(suggestions[highlightedIndex]);
            return;
          } else if (highlightedIndex === suggestions.length && inputValue.trim()) {
            e.preventDefault();
            e.stopPropagation();
            handleCreateNew();
            return;
          }
          // If nothing highlighted, let parent handle Enter
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
          setHighlightedIndex(-1);
          return;
        case 'Tab':
          // On Tab, select highlighted item if any, then let parent handle navigation
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handleSelect(suggestions[highlightedIndex]);
          }
          setIsOpen(false);
          break;
        default:
          break;
      }
    } else {
      // Dropdown is closed
      if (e.key === 'ArrowDown' && !e.ctrlKey && !e.metaKey) {
        // Open dropdown on ArrowDown when closed
        e.preventDefault();
        e.stopPropagation();
        updateDropdownPosition();
        setIsOpen(true);
        loadSuggestions(inputValue || '');
        return;
      }
    }
    
    // Pass other key events to parent for table navigation
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Click outside handler - check both container and dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedContainer = containerRef.current?.contains(event.target);
      const clickedDropdown = dropdownRef.current?.contains(event.target);
      
      if (!clickedContainer && !clickedDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if current input matches any suggestion exactly
  const hasExactMatch = suggestions.some(
    s => s.name.toLowerCase() === inputValue.trim().toLowerCase()
  );

  // Highlight matching text
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;
    
    const queryLower = query.toLowerCase();
    const textLower = text.toLowerCase();
    const index = textLower.indexOf(queryLower);
    
    if (index === -1) return text;
    
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 text-yellow-900 font-semibold">
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  };

  // Dropdown content rendered via Portal
  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      id="inline-manufacturer-listbox"
      role="listbox"
      className="fixed bg-slate-800 border border-slate-600 rounded-lg shadow-2xl overflow-hidden"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999
      }}
      data-testid="inline-manufacturer-dropdown"
    >
      {/* Compact Header */}
      <div className="px-2.5 py-1.5 bg-slate-700/50 border-b border-slate-600">
        <span className="text-xs font-medium text-slate-400">
          {inputValue.trim() ? 'Suggestions' : 'Popular'}
          {isLoading && <Loader2 className="w-3 h-3 inline ml-2 animate-spin" />}
        </span>
      </div>

      {/* Suggestions List */}
      <div className="max-h-48 overflow-y-auto">
        {suggestions.length > 0 ? (
          suggestions.map((suggestion, index) => (
            <button
              key={suggestion.name}
              id={`inline-mfr-option-${index}`}
              role="option"
              aria-selected={highlightedIndex === index}
              onMouseDown={(e) => {
                e.preventDefault(); // CRITICAL: Prevent blur from firing before click
                handleSelect(suggestion);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`w-full px-2.5 py-2 flex items-center justify-between text-left transition-colors ${
                highlightedIndex === index
                  ? 'bg-blue-600/30'
                  : 'hover:bg-slate-700'
              } ${suggestion.is_exact_match ? 'bg-emerald-600/20' : ''}`}
              data-testid={`inline-mfr-option-${suggestion.name}`}
            >
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                {suggestion.is_exact_match && (
                  <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                )}
                <span className={`text-sm truncate ${
                  suggestion.is_exact_match 
                    ? 'text-emerald-300 font-medium' 
                    : 'text-slate-200'
                }`}>
                  {highlightMatch(suggestion.name, inputValue)}
                </span>
              </div>
              <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${
                suggestion.part_count >= 10 
                  ? 'bg-blue-500/30 text-blue-300'
                  : 'bg-slate-600 text-slate-400'
              }`}>
                {suggestion.part_count}
              </span>
            </button>
          ))
        ) : isLoading ? (
          <div className="px-3 py-3 text-center text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
          </div>
        ) : inputValue.trim() ? (
          <div className="px-3 py-3 text-center text-slate-500 text-sm">
            No matches found
          </div>
        ) : null}

        {/* Create New Option */}
        {inputValue.trim() && !hasExactMatch && (
          <button
            id={`inline-mfr-option-${suggestions.length}`}
            role="option"
            aria-selected={highlightedIndex === suggestions.length}
            onMouseDown={(e) => {
              e.preventDefault(); // CRITICAL: Prevent blur from firing before click
              handleCreateNew();
            }}
            onMouseEnter={() => setHighlightedIndex(suggestions.length)}
            className={`w-full px-2.5 py-2 flex items-center space-x-2 text-left border-t border-slate-600 transition-colors ${
              highlightedIndex === suggestions.length
                ? 'bg-emerald-600/30'
                : 'hover:bg-slate-700'
            }`}
            data-testid="inline-mfr-create-new"
          >
            <div className="w-6 h-6 rounded-full bg-emerald-600/30 flex items-center justify-center flex-shrink-0">
              <Plus className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <span className="text-sm text-emerald-400 truncate">
              Create "{inputValue.trim()}"
            </span>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="relative">
      {/* Input Field - styled to match table cells */}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onMouseDown={handleMouseDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="inline-manufacturer-listbox"
        aria-activedescendant={highlightedIndex >= 0 ? `inline-mfr-option-${highlightedIndex}` : undefined}
      />

      {/* Render dropdown via Portal to escape overflow containers */}
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default InlineManufacturerAutocomplete;
